import React from 'react';
import { Placeholder, Text, RichText, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import AssetExplorer from '@component/AssetExplorer';
import * as Constants from './constants';
import { findTranslation } from '../../findTranslation';

const query = `
query Query($q: String, $top: Int, $skip: Int, $language: String, $fields: String, $filters: [FiltersInput]) {
  searchWebdam(q: $q, top: $top, skip: $skip, language: $language, fields: $fields, filters: $filters) {
    count
    facets {
      AssetSubType {
        count
        value
      }
      AssetType {
        count
        value
      }
      Campaign {
        count
        value
      }
      Keyword {
        count
        value
      }
      Language {
        count
        value
      }
      OurCauses {
        count
        value
      }
      Region {
        count
        value
      }
    }
    results {
      document {
        Id
        Filename
        Filetype
        Filesize
        Type
        MediaUrl
        AudioUrl
        Status
        LogoJpgUrl
        LogoPngUrl
        Language
        BrandcenterType
        AssetType
        AssetSubType
        OurCauses
        Campaign
        Keyword
        Region
        ThumbnailUrl
        RelatedAssetIds
        ChiliTemplateId
        Transcript
        DirectDownloadLink
        DirectLink
        EmbedCode
        EN_Title
        DE_Title
        ES_Title
        FR_Title
        IT_Title
        JA_Title
        KO_Title
        PT_Title
        ZH_Title
        EN_Description
        DE_Description
        ES_Description
        FR_Description
        IT_Description
        JA_Description
        KO_Description
        PT_Description
        ZH_Description
        EN_WhenToUse
        DE_WhenToUse
        ES_WhenToUse
        FR_WhenToUse
        IT_WhenToUse
        JA_WhenToUse
        KO_WhenToUse
        PT_WhenToUse
        ZH_WhenToUse
        DateCreated
        DateModified
        IsDeleted
        RelatedAssets
      }
      score
    }
  }
}      
`;

const AssetExplorerComponent = (props) => {
  const isEditing = props.sitecoreContext && props.sitecoreContext.pageEditing;
  const lang = props.sitecoreContext?.language?.split('-')[0]?.toUpperCase() || 'EN';
  const variable = {
    q: '*',
    top: props.fields.pageSize?.value,
    skip: 0,
    language: lang,
    fields: '',
    filters: [],
  };

  //get UI translations
  const translationManagerItemId = 'c8400712-1344-4f16-8b31-d8a270094e9d';
  const translationManagerItem = props.fields?.children?.find((e) => e.id === translationManagerItemId);
  const assetExplorerUiTranslationsItem = translationManagerItem?.fields?.assetExplorerUiTranslations?.fields;

  //get Facet translations
  const handleFindFacetTranslation = (key, value) => {
    return findTranslation(translationManagerItem, key, value);
  };

  //get Language facets
  const languageTranslationsItemId = 'f81e8824-bc87-47a6-9948-d61079b0c174';
  const languageTranslationsItem = translationManagerItem?.fields?.children?.find((c) => c.id === languageTranslationsItemId);
  const languageList = [];
  languageTranslationsItem?.fields?.children?.map((item) => {
    languageList.push(item.fields?.value?.value);
  });

  //set search box props
  const search = {
    placeholder: assetExplorerUiTranslationsItem?.searchBoxPlaceholderText?.value,
    defaultValue: '',
  };

  return (
    <>
      <AssetExplorer
        placeholder={<Placeholder name="asset-explorer-search-box" rendering={props.rendering} />}
        searchBox={search}
        graphqlQuery={query}
        graphqlVariables={variable}
        graphqlApiUri={props.fields.graphqlapiuri}
        findTranslation={handleFindFacetTranslation}
        contextLanguage={lang}
        fullContextLanguage={props.sitecoreContext?.language}
        languageList={languageList}
        shareUrl={props.fields.assetexplorersharepage}
        tabParam={props.fields.tabparam}
        //UI Labels
        downloadsLabel={assetExplorerUiTranslationsItem?.downloadsLabel?.value}
        templateLabel={assetExplorerUiTranslationsItem?.templateLabel?.value}
        siteLabel={assetExplorerUiTranslationsItem?.siteLabel.value}
        applyLabel={<Text field={assetExplorerUiTranslationsItem?.applyLabel} />}
        resetLabel={<Text field={assetExplorerUiTranslationsItem?.resetLabel} />}
        filterTextLabel={<Text field={assetExplorerUiTranslationsItem?.filterTextLabel} />}
        resetAllLabel={<Text field={assetExplorerUiTranslationsItem?.resetAllLabel} />}
        clearAllLabel={<Text field={assetExplorerUiTranslationsItem?.clearAllLabel} />}
        viewLanguagesLabel={assetExplorerUiTranslationsItem?.viewLanguagesLabel?.value}
        viewSpecificLanguageLabel={assetExplorerUiTranslationsItem?.viewSpecificLanguageLabel?.value}
        resultCountText={assetExplorerUiTranslationsItem?.resultCountText?.value}
        resultCountTextAlt={assetExplorerUiTranslationsItem?.resultCountTextAlt?.value}
        viewDetailsHoverLabel={assetExplorerUiTranslationsItem?.viewDetailsHoverLabel?.value} //todo
        paginationLabel={assetExplorerUiTranslationsItem?.paginationLabel?.value}
        gridLoadErrorText={assetExplorerUiTranslationsItem?.gridLoadErrorText?.value}
        kilobyteLabel={assetExplorerUiTranslationsItem?.kilobyteLabel?.value}
        megabyteLabel={assetExplorerUiTranslationsItem?.megabyteLabel?.value}
        gigabyteLabel={assetExplorerUiTranslationsItem?.gigabyteLabel?.value}
        //Downloads Tab
        termsOfUseDownloads={assetExplorerUiTranslationsItem?.termsOfUseDownloads?.value}
        noResultTextDownloads={assetExplorerUiTranslationsItem?.noResultTextDownloads?.value}
        //Templates Tab
        termsOfUseTemplates={assetExplorerUiTranslationsItem?.termsOfUseTemplates?.value}
        noResultTextTemplates={assetExplorerUiTranslationsItem?.noResultTextTemplates?.value}
        //Search Tab
        noResultTextSearch={assetExplorerUiTranslationsItem?.noResultTextSearch?.value}
        //Modal Labels
        viewDetailsLabel={assetExplorerUiTranslationsItem?.viewDetailsLabel?.value}
        downloadButtonLabel={assetExplorerUiTranslationsItem?.downloadButtonLabel?.value}
        fallbackImage={assetExplorerUiTranslationsItem?.fallbackImage?.value?.src}
        whenToUseLabel={assetExplorerUiTranslationsItem?.whenToUseLabel?.value}
        createButtonLabel={assetExplorerUiTranslationsItem?.createButtonLabel?.value}
        modalNoteLabel={assetExplorerUiTranslationsItem?.modalNoteLabel?.value}
        modalNoteText={assetExplorerUiTranslationsItem?.modalNoteText?.value}
        relatedFilesLabel={assetExplorerUiTranslationsItem?.relatedFilesLabel?.value}
        shareLabel={assetExplorerUiTranslationsItem?.shareLabel?.value}
        shareLinkLabel={assetExplorerUiTranslationsItem?.shareLinkLabel?.value}
        copyLabel={assetExplorerUiTranslationsItem?.copyLabel?.value}
        copiedLabel={assetExplorerUiTranslationsItem?.copiedLabel?.value}
        embedLabel={assetExplorerUiTranslationsItem?.embedLabel?.value}
        chooseFileTypeLabel={assetExplorerUiTranslationsItem?.chooseFileTypeLabel?.value}
        placeholderSelectText={assetExplorerUiTranslationsItem?.placeholderSelectText?.value} //todo do we still need this?
        fileNameLabel={assetExplorerUiTranslationsItem?.fileNameLabel?.value}
        fileTypeLabel={assetExplorerUiTranslationsItem?.fileTypeLabel?.value}
        fileSizeLabel={assetExplorerUiTranslationsItem?.fileSizeLabel?.value}
        dateCreatedLabel={assetExplorerUiTranslationsItem?.dateCreatedLabel?.value} //todo do we still need this?
        assetLanguageLabel={assetExplorerUiTranslationsItem?.assetLanguageLabel?.value}
        //Asset Explorer Settings
        templateCreateLink={props.fields?.templateCreateLink?.value}
        pageSize={props.fields.pageSize?.value}
      />
    </>
  );
};
export default withSitecoreContext()(AssetExplorerComponent);
